import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import type { StationChargeFilters, TrafficFilters } from '../constants';
import { STATION_CHARGE_FILTERS, TRAFFIC_FILTERS } from '../constants';
import {
  ALL_PLACES_VISIBLE,
  PLACE_SITE_TYPE_IDS,
  type PlaceSiteTypeId,
  type PlaceVisibility,
} from '../constants/placeSiteTypes';
import {
  POI_FILTER_TRACKING_DEBOUNCE_MS,
  POI_MIXPANEL_EVENTS,
  type PoiFiltersAppliedPayload,
} from '../utils/mixpanelEvents';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface LayerVisibilityState {
  traffic: Record<TrafficFilters, boolean>;
  station: Record<StationChargeFilters, boolean>;
  isElectraVisible: boolean;
  isLowHpcVisible: boolean;
  /** The wide-zoom session field. Hidden until opened. Stations stay when this is off. */
  isClustersVisible: boolean;
  places: PlaceVisibility;
  /** Workspace location pins. Deals stay when this is off. */
  locationsVisible: boolean;
}

// ---------------------------------------------------------------------------
// Handlers shape
// ---------------------------------------------------------------------------

export interface LayerVisibilityHandlers {
  toggleTraffic: (id: TrafficFilters) => void;
  /** Hide all traffic ranges if any is visible; otherwise show all. */
  toggleTrafficGroup: () => void;
  toggleStation: (label: StationChargeFilters) => void;
  /** Hide all station labels if any is visible; otherwise show all. */
  toggleStationGroup: () => void;
  toggleElectra: () => void;
  toggleLowHpc: () => void;
  /** Show or hide the wide-zoom session clusters, leaving the stations as they are. */
  toggleClusters: () => void;
  togglePlace: (id: PlaceSiteTypeId) => void;
  /** Hide every place chip if any is visible; otherwise show all. */
  togglePlaceGroup: () => void;
  /** Hide the workspace location pins if they are showing; otherwise show them. */
  toggleLocations: () => void;
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseLayerVisibilityFilters {
  state: LayerVisibilityState;
  handlers: LayerVisibilityHandlers;
}

// ---------------------------------------------------------------------------
// Default state — single-sourced from constants so it cannot drift
// ---------------------------------------------------------------------------

export const DEFAULT_LAYER_VISIBILITY: LayerVisibilityState = {
  traffic: Object.fromEntries(TRAFFIC_FILTERS.map((id) => [id, true])) as Record<
    TrafficFilters,
    boolean
  >,
  station: Object.fromEntries(STATION_CHARGE_FILTERS.map((label) => [label, true])) as Record<
    StationChargeFilters,
    boolean
  >,
  isElectraVisible: true,
  isLowHpcVisible: true,
  isClustersVisible: false,
  places: ALL_PLACES_VISIBLE,
  locationsVisible: true,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLayerVisibilityFilters(): UseLayerVisibilityFilters {
  const [state, setState] = useState<LayerVisibilityState>(DEFAULT_LAYER_VISIBILITY);
  const trackAction = useMixpanel((s) => s.trackAction);

  // The handlers are contractually stable (see `handlers` below), so they cannot
  // close over `state` to report the value they are toggling to.
  const stateRef = useRef(state);
  stateRef.current = state;

  const trackToggle = useCallback(
    (layer: string, scope: 'single' | 'group', visible: boolean) => {
      trackAction(ACTION_TRACKED.MAP_LAYER_TOGGLED, { layer, scope, visible });
    },
    [trackAction],
  );

  const toggleTraffic = useCallback(
    (id: TrafficFilters) => {
      trackToggle(`traffic:${id}`, 'single', !stateRef.current.traffic[id]);
      setState((prev) => ({
        ...prev,
        traffic: { ...prev.traffic, [id]: !prev.traffic[id] },
      }));
    },
    [trackToggle],
  );

  const toggleTrafficGroup = useCallback(() => {
    trackToggle('traffic', 'group', !Object.values(stateRef.current.traffic).some(Boolean));
    setState((prev) => {
      const anyVisible = Object.values(prev.traffic).some(Boolean);
      const next = Object.fromEntries(TRAFFIC_FILTERS.map((id) => [id, !anyVisible])) as Record<
        TrafficFilters,
        boolean
      >;
      return { ...prev, traffic: next };
    });
  }, [trackToggle]);

  const toggleStation = useCallback(
    (label: StationChargeFilters) => {
      trackToggle(`station:${label}`, 'single', !stateRef.current.station[label]);
      setState((prev) => ({
        ...prev,
        station: { ...prev.station, [label]: !prev.station[label] },
      }));
    },
    [trackToggle],
  );

  // The whole "Competitive intelligence" section, clusters included: the header
  // dims when nothing in it is showing, so the eye must hide the field too.
  const toggleStationGroup = useCallback(() => {
    const current = stateRef.current;
    const anyVisibleNow =
      Object.values(current.station).some(Boolean) ||
      current.isElectraVisible ||
      current.isLowHpcVisible ||
      current.isClustersVisible;
    trackToggle('station', 'group', !anyVisibleNow);
    setState((prev) => {
      const anyVisible =
        Object.values(prev.station).some(Boolean) ||
        prev.isElectraVisible ||
        prev.isLowHpcVisible ||
        prev.isClustersVisible;
      const next = Object.fromEntries(
        STATION_CHARGE_FILTERS.map((label) => [label, !anyVisible]),
      ) as Record<StationChargeFilters, boolean>;
      return {
        ...prev,
        station: next,
        isElectraVisible: !anyVisible,
        isLowHpcVisible: !anyVisible,
        isClustersVisible: !anyVisible,
      };
    });
  }, [trackToggle]);

  const toggleElectra = useCallback(() => {
    trackToggle('electra', 'single', !stateRef.current.isElectraVisible);
    setState((prev) => ({ ...prev, isElectraVisible: !prev.isElectraVisible }));
  }, [trackToggle]);

  const toggleLowHpc = useCallback(() => {
    trackToggle('low_hpc', 'single', !stateRef.current.isLowHpcVisible);
    setState((prev) => ({ ...prev, isLowHpcVisible: !prev.isLowHpcVisible }));
  }, [trackToggle]);

  const toggleClusters = useCallback(() => {
    trackToggle('competitor_clusters', 'single', !stateRef.current.isClustersVisible);
    setState((prev) => ({ ...prev, isClustersVisible: !prev.isClustersVisible }));
  }, [trackToggle]);

  // Scope is the last toggle's. The selection itself cannot say whether it came from one chip.
  const placeScopeRef = useRef<PoiFiltersAppliedPayload['scope']>('single');
  const placesUntrackedRef = useRef(true);
  useEffect(() => {
    if (placesUntrackedRef.current) {
      placesUntrackedRef.current = false;
      return;
    }
    const places = state.places;
    const timer = setTimeout(() => {
      const visibleTypes = PLACE_SITE_TYPE_IDS.filter((id) => places[id]);
      const payload: PoiFiltersAppliedPayload = {
        visible_types: visibleTypes,
        visible_count: visibleTypes.length,
        hidden_count: PLACE_SITE_TYPE_IDS.length - visibleTypes.length,
        scope: placeScopeRef.current,
      };
      trackAction(POI_MIXPANEL_EVENTS.FILTERS_APPLIED, payload);
    }, POI_FILTER_TRACKING_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [state.places, trackAction]);

  const togglePlace = useCallback(
    (id: PlaceSiteTypeId) => {
      placeScopeRef.current = 'single';
      trackToggle(`place:${id}`, 'single', !stateRef.current.places[id]);
      setState((prev) => ({
        ...prev,
        places: { ...prev.places, [id]: !prev.places[id] },
      }));
    },
    [trackToggle],
  );

  const toggleLocations = useCallback(() => {
    trackToggle('locations', 'group', !stateRef.current.locationsVisible);
    setState((prev) => ({ ...prev, locationsVisible: !prev.locationsVisible }));
  }, [trackToggle]);

  const togglePlaceGroup = useCallback(() => {
    placeScopeRef.current = 'group';
    const anyVisible = Object.values(stateRef.current.places).some(Boolean);
    trackToggle('place', 'group', !anyVisible);
    setState((prev) => {
      const show = !Object.values(prev.places).some(Boolean);
      const places = Object.fromEntries(
        PLACE_SITE_TYPE_IDS.map((id) => [id, show]),
      ) as PlaceVisibility;
      return { ...prev, places };
    });
  }, [trackToggle]);

  // Stabilise the handlers object identity (the inner callbacks are already
  // stable). Lets memoised consumers — e.g. the map card in a later phase —
  // avoid re-rendering when only `state` changes (contract: stable handlers).
  const handlers = useMemo<LayerVisibilityHandlers>(
    () => ({
      toggleTraffic,
      toggleTrafficGroup,
      toggleStation,
      toggleStationGroup,
      toggleElectra,
      toggleLowHpc,
      toggleClusters,
      togglePlace,
      togglePlaceGroup,
      toggleLocations,
    }),
    [
      toggleTraffic,
      toggleTrafficGroup,
      toggleStation,
      toggleStationGroup,
      toggleElectra,
      toggleLowHpc,
      toggleClusters,
      togglePlace,
      togglePlaceGroup,
      toggleLocations,
    ],
  );

  return { state, handlers };
}
